import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Link as MuiLink } from '@mui/material';
import Loader from 'components/ui/LoadingIndicator';
import AdminList, {
    ArticleCountCellRenderer,
    NameCellRenderer,
    ReadOnlyLabelCellRenderer,
} from '../../ui/admin/AdminList';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import { GET_CHANNELS_LIST } from '../../../gql/channels';
import {
    GET_CHANNEL_GROUPS,
    UPDATE_CHANNEL_GROUP_ASSIGNMENTS,
} from '../../../gql/channelGroups';
import { sortArray } from '../../../utils/sorting.utils';
import { convertToDate, formatDisplayDate } from '../../../utils/date.utils';
import PremiumChannelFilter from '../../ui/admin/PremiumChannelFilter';
import TableRowSelect from '../../ui/admin/TableRowSelect';
import {
    PremiumChannelKind,
    PremiumChannelType,
    ResearchRole,
} from '../../../shared/global-enums';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../../config';
import { formatPlural } from '../../../utils/string.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import {
    MessageTypes,
    useNotification,
} from '../../providers/NotificationProvider';
import { useUser } from '../../providers/UserProvider';
import {
    Channel,
    ChannelGroup,
    ChannelState,
    ChannelTypes,
} from '../../../shared/types';
import { isChannelAdmin } from '../../../utils/channel.utils';
import { ChannelLabel } from '../../ui/ChannelLabel';
import { Link as RouterLink } from 'react-router-dom';

const title = 'Channels';
const name = 'premium-channels';

const PremiumChannelsList = () => {
    const { hasScope } = useUser();
    const isReadOnly = ![ResearchRole.Administrator, ResearchRole.Editor].some(
        hasScope
    );
    const isAdmin = [ResearchRole.Administrator].some(hasScope);
    const { accessToken } = useAuth0Data();
    const neonUserId =
        jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];
    const [premiumChannelKind, setPremiumChannelKind] = useState(
        PremiumChannelKind.All
    );
    const [channelType, setChannelType] = useState(PremiumChannelType.All);
    const [groupAssignments, setGroupAssignments] = useState<
        Record<string, string>
    >({});
    const groupAssignmentsRef = useRef<Record<string, string>>({});
    const [initialGroupAssignments, setInitialGroupAssignments] = useState<
        Record<string, string>
    >({});
    const [gridApi, setGridApi] = useState<GridApi | undefined>();
    const applyGroupAssignments = useCallback(
        (updater: React.SetStateAction<Record<string, string>>) => {
            const prev = groupAssignmentsRef.current;
            const next =
                typeof updater === 'function'
                    ? (
                          updater as (
                              prevState: Record<string, string>
                          ) => Record<string, string>
                      )(prev)
                    : updater;

            groupAssignmentsRef.current = next;
            setGroupAssignments(next);

            if (isAdmin && gridApi) {
                gridApi.refreshCells({
                    force: true,
                    columns: ['channel-group-col'],
                });
            }
        },
        [gridApi, isAdmin]
    );
    const { showMessage } = useNotification();
    const { loading, error, data } = useQuery(GET_CHANNELS_LIST, {
        fetchPolicy: 'network-only',
    });
    const { data: groupsData, loading: groupsLoading } = useQuery(
        GET_CHANNEL_GROUPS,
        {
            skip: !isAdmin,
            fetchPolicy: 'network-only',
            onCompleted: (result) => {
                const channelGroups = result?.channelGroups ?? [];
                const groups: ChannelGroup[] = channelGroups ?? [];
                const map: Record<string, string> = {};

                groups.forEach((group) => {
                    if (!Array.isArray(group.channelIds)) return;

                    group.channelIds.forEach((channelId) => {
                        map[channelId] = group._id;
                    });
                });

                applyGroupAssignments(map);
                setInitialGroupAssignments(map);
            },
        }
    );

    const [saveAssignments, { loading: savingAssignments }] = useMutation(
        UPDATE_CHANNEL_GROUP_ASSIGNMENTS
    );
    const articleFilter = useArticleFilter();

    const onGridApiReady = useCallback((api: GridApi) => {
        setGridApi(api);
    }, []);

    const handleSave = useCallback(async () => {
        const allChannels: Channel[] = data?.allChannels ?? [];
        const currentAssignments = groupAssignmentsRef.current;
        const assignments: {
            channelId: string;
            groupId: string | null;
        }[] = allChannels.map((channel: Channel) => ({
            channelId: channel._id,
            groupId: currentAssignments[channel._id] || null,
        }));

        try {
            const response = await saveAssignments({
                variables: { assignments },
            });
            if (response?.data?.updateChannelGroupAssignments) {
                const savedAssignments = assignments.reduce(
                    (acc: Record<string, string>, assignment) => {
                        acc[assignment.channelId] = assignment.groupId || '';
                        return acc;
                    },
                    {}
                );
                setInitialGroupAssignments(savedAssignments);
                showMessage(
                    'Channel group assignments have been updated.',
                    MessageTypes.info
                );
                return;
            }
        } catch (error) {
            showMessage(
                'Failed to save group changes, please try again',
                MessageTypes.error
            );
        }
    }, [data, saveAssignments, showMessage]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                colId: 'channel-name-col',
                headerName: 'Channel name',
                field: 'name',
                cellRenderer: (props: any) =>
                    isAdmin ||
                    isChannelAdmin(props.data.adminUsers, neonUserId) ? (
                        <NameCellRenderer {...props} apiName={name} />
                    ) : (
                        <ReadOnlyLabelCellRenderer {...props} />
                    ),
                width: 260,
                menuTabs: [],
            },
            {
                colId: 'channel-owner-col',
                headerName: 'Channel Owner',
                field: 'owner.name',
                width: 190,
                minWidth: 170,
                menuTabs: [],
                cellRenderer: (props: any) =>
                    (isAdmin ||
                        isChannelAdmin(props.data.adminUsers, neonUserId)) &&
                    props.data.ownerType === 'desk' ? (
                        <MuiLink
                            component={RouterLink}
                            to={`${baseUrls.publisher.main}/desks/edit/${props.data.owner._id}`}
                            underline="hover"
                        >
                            {props.value}
                        </MuiLink>
                    ) : (
                        <ReadOnlyLabelCellRenderer {...props} />
                    ),
            },
            {
                headerName: 'Channel type',
                field: 'type',
                width: 160,
                minWidth: 140,
                menuTabs: [],
                cellRenderer: (props: any) => (
                    <ChannelLabel type={props.data.type} />
                ),
            },
            {
                colId: 'channel-articles-col',
                headerName: 'Articles',
                field: 'articleCount',
                cellRenderer: (props: ICellRendererParams) => (
                    <ArticleCountCellRenderer
                        value={
                            props.data.state === ChannelState.Archived
                                ? props.data.archivedArticleCount
                                : props.value
                        }
                        filter={articleFilter}
                        context={props.context}
                        filterData={{
                            _id: props.data._id,
                            name: props.data.name,
                            type:
                                props.data.state === ChannelState.Archived
                                    ? 'archived-channel'
                                    : 'channel',
                        }}
                    />
                ),
                width: 130,
                minWidth: 120,
                menuTabs: [],
            },
            {
                colId: 'channel-subscribers-col',
                headerName: 'Subscribers',
                field: 'name',
                cellRenderer: (props: any) =>
                    (isAdmin ||
                        isChannelAdmin(props.data.adminUsers, neonUserId)) &&
                    props.data.state === ChannelState.Active ? (
                        <MuiLink
                            component={RouterLink}
                            to={`${baseUrls.publisher.premiumChannels.subscribersStatsMain}/${props.data._id}`}
                            underline="hover"
                        >
                            {formatPlural(
                                props.data.channelActiveUsersCount,
                                'user'
                            )}
                        </MuiLink>
                    ) : (
                        formatPlural(props.data.channelActiveUsersCount, 'user')
                    ),
                width: 150,
                minWidth: 130,
                menuTabs: [],
            },
            {
                colId: 'channel-created-col',
                headerName: 'Created',
                field: 'createdAt',
                cellRenderer: (props: any) => (
                    <>{formatDisplayDate(convertToDate(props.value), true)}</>
                ),
                width: 140,
                minWidth: 120,
                menuTabs: [],
            },
            ...(isAdmin
                ? [
                      {
                          colId: 'channel-group-col',
                          headerName: 'Group',
                          field: 'groupId',
                          sortable: false,
                          cellClass: 'channel-group-cell',
                          width: 170,
                          minWidth: 150,
                          menuTabs: [],
                          cellRenderer: (props: any) => {
                              const channelId = props.data._id;
                              const groups: ChannelGroup[] =
                                  groupsData?.channelGroups ?? [];
                              return (
                                  <TableRowSelect
                                      value={
                                          groupAssignmentsRef.current[
                                              channelId
                                          ] || ''
                                      }
                                      groups={groups}
                                      onChange={(value) => {
                                          applyGroupAssignments((prev) => ({
                                              ...prev,
                                              [channelId]: value,
                                          }));
                                      }}
                                  />
                              );
                          },
                      },
                  ]
                : []),
            // eslint-disable-next-line react-hooks/exhaustive-deps
        ],
        [
            isAdmin,
            isReadOnly,
            neonUserId,
            articleFilter,
            groupsData,
            applyGroupAssignments,
        ]
    );

    const channels = (data?.allChannels as Channel[]) ?? [];
    const hasAssignmentChanges =
        isAdmin &&
        channels.some(
            (channel: Channel) =>
                (groupAssignments[channel._id] || '') !==
                (initialGroupAssignments[channel._id] || '')
        );

    const sortedChannels = useMemo(() => {
        const kindFiltered =
            premiumChannelKind === PremiumChannelKind.All
                ? channels.filter((item) => item.state === ChannelState.Active)
                : premiumChannelKind === PremiumChannelKind['My Channels']
                  ? channels.filter(
                        (item) =>
                            item.owner._id === neonUserId &&
                            item.state === ChannelState.Active
                    )
                  : channels.filter(
                        (item) => item.state === ChannelState.Archived
                    );

        const typeFiltered =
            channelType === PremiumChannelType.All
                ? kindFiltered
                : kindFiltered.filter((ch) =>
                      channelType === PremiumChannelType.Premium
                          ? ch.type === ChannelTypes.premium
                          : ch.type === ChannelTypes.freeClosed ||
                            ch.type === ChannelTypes.freeOpen
                  );

        return sortArray(typeFiltered, 'name', true);
    }, [channels, premiumChannelKind, channelType, neonUserId]);

    if (loading || (isAdmin && groupsLoading)) {
        return <Loader />;
    }

    return (
        <AdminList
            title={title}
            name={name}
            items={sortedChannels}
            loading={loading}
            error={error}
            columns={columnDefs}
            apiCallback={onGridApiReady}
            showAdd={!isReadOnly && isAdmin}
            addButtonLabel="New channel"
            entityForCaptionText="channels"
            toolbar={
                <PremiumChannelFilter
                    premiumChannelKind={premiumChannelKind}
                    setPremiumChannelKind={setPremiumChannelKind}
                    channelType={channelType}
                    setChannelType={setChannelType}
                />
            }
            footer={
                isAdmin ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button
                            id="save-channel-group-assignments-btn"
                            variant="contained"
                            disabled={
                                savingAssignments || !hasAssignmentChanges
                            }
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </Box>
                ) : undefined
            }
        />
    );
};

export default PremiumChannelsList;
